/**
 * hooks/useBrokerFeeDetails.ts
 * Hook for loading heavy broker fee configuration data
 * 
 * Provides on-demand loading of detailed fee structures when user
 * needs to view fee details or configure a specific broker.
 * Separated from selection for performance optimization.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type {
  BrokerFeeCatalogEntry,
  BrokerFeeLoadingState,
  BrokerFeeConfiguration
} from '@/types/reference/BrokerFeeCatalog';
import {
  getBrokerFeeDetails,
  hasBrokerFeeDetails
} from '@/data/brokerFeeCatalog';

// Simple in-memory cache for broker fee details
const feeDetailsCache = new Map<string, BrokerFeeCatalogEntry>();

/**
 * Hook for loading fee details for a specific broker
 * Now with caching to eliminate loading flash on navigation
 */
export const useBrokerFeeDetails = (
  brokerId: string | null
): BrokerFeeLoadingState => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<BrokerFeeCatalogEntry | null>(null);
  const loadingRef = useRef(false);
  const prevBrokerIdRef = useRef<string | null>(null);

  // Synchronously check cache and update state when brokerId changes
  if (brokerId !== prevBrokerIdRef.current) {
    prevBrokerIdRef.current = brokerId;

    if (!brokerId) {
      if (data !== null) setData(null);
      if (error !== null) setError(null);
      if (loading !== false) setLoading(false);
    } else if (feeDetailsCache.has(brokerId)) {
      const cachedData = feeDetailsCache.get(brokerId);
      if (cachedData) {
        if (data !== cachedData) setData(cachedData);
        if (error !== null) setError(null);
        if (loading !== false) setLoading(false);
      }
    }
  }

  useEffect(() => {
    if (!brokerId) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    // Check cache first - instant return if cached
    if (feeDetailsCache.has(brokerId)) {
      const cachedData = feeDetailsCache.get(brokerId);
      if (cachedData) {
        setData(cachedData);
        setError(null);
        setLoading(false);
        return;
      }
    }

    // Prevent duplicate loads
    if (loadingRef.current) return;

    // Load fee details asynchronously
    const loadFeeDetails = async () => {
      loadingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        // No artificial delay - load immediately from local data
        const feeDetails = getBrokerFeeDetails(brokerId);

        if (!feeDetails) {
          throw new Error(`Fee details not found for broker: ${brokerId}`);
        }

        // Cache the result for future use
        feeDetailsCache.set(brokerId, feeDetails);
        setData(feeDetails);
      } catch (err) {
        setError(err as Error);
        setData(null);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    };

    loadFeeDetails();
  }, [brokerId]);

  return { loading, error, data };
};

/**
 * Hook for checking if broker has fee details available
 */
export const useHasBrokerFeeDetails = (brokerId: string | null): boolean => {
  return useMemo(() => {
    if (!brokerId) return false;
    return hasBrokerFeeDetails(brokerId);
  }, [brokerId]);
};

/**
 * Hook for getting fee configurations by asset class
 */
export const useBrokerFeesByAssetClass = (
  brokerId: string | null,
  assetClass: string | null
): {
  loading: boolean;
  error: Error | null;
  feeConfigurations: BrokerFeeConfiguration[];
} => {
  const { loading, error, data } = useBrokerFeeDetails(brokerId);
  
  const feeConfigurations = useMemo(() => {
    if (!data || !assetClass) return [];
    
    return data.feeConfigurations.filter(config => 
      config.category?.assetClass === assetClass
    );
  }, [data, assetClass]);

  return { loading, error, feeConfigurations };
};

/**
 * Hook for preloading fee details for multiple brokers
 */
export const usePreloadBrokerFees = () => {
  const [preloadedBrokers, setPreloadedBrokers] = useState<Set<string>>(new Set());
  
  const preloadFees = useCallback(async (brokerIds: string[]) => {
    const newlyLoaded = new Set<string>();
    
    for (const brokerId of brokerIds) {
      if (!preloadedBrokers.has(brokerId) && hasBrokerFeeDetails(brokerId)) {
        // Simulate preloading (in production, might cache API responses)
        await new Promise(resolve => setTimeout(resolve, 50));
        newlyLoaded.add(brokerId);
      }
    }
    
    if (newlyLoaded.size > 0) {
      setPreloadedBrokers(prev => new Set([...prev, ...newlyLoaded]));
    }
  }, [preloadedBrokers]);

  const isPreloaded = useCallback((brokerId: string) => {
    return preloadedBrokers.has(brokerId);
  }, [preloadedBrokers]);

  return { preloadFees, isPreloaded };
};

/**
 * Hook for fee configurations search across all brokers
 */
export const useFeeConfigurationSearch = (searchTerm: string) => {
  const [results, setResults] = useState<Array<{
    brokerId: string;
    brokerName: string;
    configuration: BrokerFeeConfiguration;
  }>>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setResults([]);
      return;
    }

    const searchFees = async () => {
      setLoading(true);
      
      // Simulate search delay
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // TODO: Implement actual search across all fee configurations
      // This would search through all broker fee data
      setResults([]);
      setLoading(false);
    };

    searchFees();
  }, [searchTerm]);

  return { results, loading };
};

/**
 * Hook for validating fee configuration data
 * TODO: Re-implement when fee configuration structure is finalized
 */
export const useFeeConfigurationValidator = () => {
  const validateConfiguration = useCallback((config: BrokerFeeConfiguration): {
    isValid: boolean;
    errors: string[];
  } => {
    // Simplified validation for now
    const errors: string[] = [];
    
    if (!config.id) errors.push('Configuration ID is required');
    if (!config.name) errors.push('Configuration name is required');
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }, []);

  return { validateConfiguration };
};